import { useState, useEffect, useContext, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { accountService } from '../services/accountService';
import UpgradeModal from '../components/UpgradeModal';
import { Trans, useLingui } from '@lingui/react/macro';
import axios from 'axios';
import { captureArrivalCodes, registerPayloadCodes } from '../lib/arrival-codes';
import { stripCodeParams, hasCodeParams, NO_CODES } from '../lib/codes';
import type { ArrivalCodes } from '../lib/codes';
import { useMaxOffer } from '../lib/useMaxOffer';
import { offerPricing, promoDuration } from '../services/maxOfferService';
import { trialCallUrl } from '../constants/maxTrial';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:42026';

// Mirror the API's signup-validator so client feedback matches what the server
// enforces. Kept intentionally strict on whitespace/HTML chars.
const EMAIL_PATTERN = /^[^\s@<>"'`]+@[^\s@<>"'`]+\.[^\s@<>"'`]+$/;

// Each password requirement with whether the current value satisfies it, so the
// signup form can show exactly which rule(s) fail rather than a generic error.
const passwordRules = (pw: string) => [
  { id: 'length', met: pw.length >= 8 },
  { id: 'uppercase', met: /[A-Z]/.test(pw) },
  { id: 'lowercase', met: /[a-z]/.test(pw) },
  { id: 'number', met: /[0-9]/.test(pw) },
];

type FieldErrors = {
  firstName?: string;
  lastName?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
};

const fieldErrorStyle: React.CSSProperties = { color: '#ff6b6b', fontSize: '11px', marginTop: '0.4rem' };

/**
 * What POST /api/user/register said about the trial code we sent. null when no
 * code was sent; redeemed:false with a reason when a code was sent and did not
 * apply, which is NOT an error: the account is real either way.
 */
type RegisterTrial = {
  redeemed: boolean;
  kind: 'stripe' | 'enterprise' | null;
  code: string | null;
  trialDays: number | null;
  requiresCard: boolean;
  message: string | null;
} | null;

export default function SignupPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLingui();
  const { user, ssoLogin, refreshUser } = useContext(AuthContext);
  // EPIC-15 (PLG): the wizard has no payment step. identity -> verify-code ->
  // plan, where "plan" is a choice, not a paywall: Pro is free (default,
  // one click through) and Max opens the immediate-charge checkout modal.
  const [step, setStep] = useState<'identity' | 'verify-code' | 'plan'>('identity');
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  // Which of the modal's two Max paths opens: a card, or a company-wide trial.
  const [upgradePath, setUpgradePath] = useState<'card' | 'trial'>('card');
  // True only in the session that just completed register(): existing users
  // landing on /signup are routed away instead of re-seeing the plan step.
  const justRegisteredRef = useRef(false);
  const [resendBusy, setResendBusy] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  // Verify-before-create: the 6-digit emailed code must be proven before the
  // account exists; register() then carries the resulting signupToken.
  const [signupCode, setSignupCode] = useState('');
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [codeError, setCodeError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  // Max prices for the plan cards; the checkout modal fetches its own config.
  const [maxPrices, setMaxPrices] = useState<{ monthly: string | null; annual: string | null } | null>(null);
  // The three codes the visitor arrived with, captured before anything can
  // navigate. Initialised from the query string merged over sessionStorage, so
  // a refresh or the round trip to Google does not lose a partner's credit.
  const [codes, setCodes] = useState<ArrivalCodes>(NO_CODES);
  // What register() said about the trial code. An enterprise code that
  // redeemed means this account is ALREADY on Max and must not be shown a
  // card; a stripe-kind code still needs one.
  const [registerTrial, setRegisterTrial] = useState<RegisterTrial>(null);
  // Read by the routing effect, which must not take registerTrial as a
  // dependency: it routes on the account, and re-running it mid-render would
  // bounce the customer off the confirmation they just earned.
  const enterpriseTrialRef = useRef(false);
  const [_account, setAccount] = useState<any>(null);
  const [loadingAccount, setLoadingAccount] = useState(false);
  const velocityLinesContainerRef = useRef<HTMLDivElement>(null);

  /**
   * Capture ?promo=, ?ref= (also ?partner=, ?aff=) and ?trial= on arrival.
   *
   * First effect on the page on purpose: the codes have to be on disk before
   * anything can navigate away, because the SSO buttons leave the origin
   * entirely and come back with a bare query string.
   *
   * Then strip the code keys out of the address bar while leaving utm_*,
   * fbclid and everything else alone. Stripping matters for attribution, not
   * tidiness: a customer who copies the URL out of the bar and sends it to a
   * colleague would otherwise re-credit that colleague's company to the same
   * partner. Stripping the utm_* alongside would break the campaign reporting
   * the codes exist to feed, so those stay.
   */
  useEffect(() => {
    const search = location.search || window.location.search;
    setCodes(captureArrivalCodes(search));
    if (hasCodeParams(search)) {
      navigate({ pathname: location.pathname, search: stripCodeParams(search) }, { replace: true });
    }
    // Arrival is a one-time event. Re-running on every location change would
    // re-capture from the stripped URL and clear nothing but its own work.
  }, []);

  useEffect(() => {
    if (!velocityLinesContainerRef.current) return;
    const container = velocityLinesContainerRef.current;
    container.innerHTML = '';
    for (let i = 0; i < 20; i++) {
      const line = document.createElement('div');
      line.className = 'velocity-line';
      line.style.left = Math.random() * 100 + 'vw';
      line.style.animationDelay = Math.random() * 5 + 's';
      line.style.animationDuration = (Math.random() * 5 + 5) + 's';
      container.appendChild(line);
    }
    return () => { container.innerHTML = ''; };
  }, [step]);

  useEffect(() => {
    if (user) {
      setLoadingAccount(true);
      Promise.all([
        accountService.getAccountStatus(),
        // Check onboarding status to decide where to route the user
        import('../services/onboardingService').then(m => m.onboardingService.getStatus())
      ])
        .then(([data, onboardingDone]) => {
          setAccount(data);
          const live = data?.status === 'pro' || data?.effectiveStatus === 'pro' || data?.subscriptionId || data?.status === 'trial';
          // A signup that claimed a company/prepaid seat is already on its
          // final plan; pitching Max at it would be wrong (members don't buy).
          const seatCovered = data?.effectiveTier === 'max' || data?.licenseType === 'prepaid';
          // An enterprise trial code just granted Max company-wide. That looks
          // exactly like a covered seat, so without this the customer would be
          // routed straight past the one screen that tells them their trial is
          // live and when it ends.
          if (live && (!justRegisteredRef.current || seatCovered) && !enterpriseTrialRef.current) {
            // An existing signed-in account has no business on the wizard.
            navigate(onboardingDone ? '/dashboard' : '/onboarding');
          } else {
            // Fresh registration (or a legacy account that stalled before the
            // old card step): show the plan choice. Free continues with one
            // click; Max opens the immediate-charge checkout. Either way the
            // account already exists and is live — nothing here can lose it.
            setStep('plan');
            accountService.getStripeConfig().then((cfg) => {
              setMaxPrices({
                monthly: cfg?.tiers?.max?.monthly?.formatted ?? null,
                annual: cfg?.tiers?.max?.annual?.formatted ?? null,
              });
            });
          }
        })
        .catch(() => setAccount(null))
        .finally(() => setLoadingAccount(false));
    }
  }, [user, navigate]);

  const handleIdentitySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitAttempted(true);

    // Validate each field independently so the user sees exactly what's wrong,
    // inline, instead of a single generic error banner.
    const errs: FieldErrors = {};
    if (!formData.firstName.trim()) errs.firstName = t`First name is required`;
    else if (/[<>]/.test(formData.firstName)) errs.firstName = t`First name contains invalid characters`;
    if (!formData.lastName.trim()) errs.lastName = t`Last name is required`;
    else if (/[<>]/.test(formData.lastName)) errs.lastName = t`Last name contains invalid characters`;
    if (!formData.email.trim()) errs.email = t`Email is required`;
    else if (!EMAIL_PATTERN.test(formData.email.trim())) errs.email = t`Please enter a valid email address`;
    if (!passwordRules(formData.password).every((r) => r.met)) {
      // Specifics are shown in the live checklist below the field.
      errs.password = t`Password does not meet the requirements below`;
    }
    if (formData.confirmPassword !== formData.password) {
      errs.confirmPassword = t`Passwords do not match`;
    }

    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) return;

    // Verify-before-create: no account exists yet. Send the 6-digit code and
    // collect it before register() ever runs.
    try {
      await axios.post(`${API_BASE_URL}/api/user/signup/start`, { email: formData.email.trim() });
      setSignupCode('');
      setCodeError('');
      setResendCooldown(60);
      setStep('verify-code');
    } catch (err: any) {
      const data = err.response?.data;
      if (data?.field) {
        setFieldErrors((prev) => ({ ...prev, [data.field as keyof FieldErrors]: data.error }));
      } else {
        setError(data?.error || err.message || t`Could not send a verification code`);
      }
    }
  };

  const handleVerifyCode = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!/^\d{6}$/.test(signupCode.trim())) {
      setCodeError(t`Enter the 6-digit code from the email.`);
      return;
    }
    setVerifyBusy(true);
    setCodeError('');
    try {
      const verify = await axios.post(`${API_BASE_URL}/api/user/signup/verify`, {
        email: formData.email.trim(),
        code: signupCode.trim()
      });
      const signupToken = verify.data?.signupToken;
      if (!verify.data?.verified || !signupToken) {
        setCodeError(verify.data?.error || t`That code is invalid or has expired.`);
        return;
      }

      // Mailbox proven, so create the account (born verified, signed in
      // immediately). The three arrival codes ride along: promoCode sets
      // PRICE, referralCode is ATTRIBUTION only, trialCode grants
      // ENTITLEMENT. All three are optional and the API drops a bad one
      // silently, so none of them can cost this customer their account.
      const response = await axios.post(`${API_BASE_URL}/api/user/register`, {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        password: formData.password,
        signupToken,
        ...registerPayloadCodes(codes)
      });

      if (response.data?.token) {
        // Prepaid company seats were claimed server-side at registration; the
        // user-effect routes on account status. Mark this session as the one
        // that registered so the plan step is shown exactly once.
        justRegisteredRef.current = true;
        // What became of the trial code decides the plan step: an enterprise
        // code that redeemed means Max is already live company-wide and
        // pitching a card would be wrong.
        const trial = response.data.trial;
        setRegisterTrial(trial
          ? {
            redeemed: Boolean(trial.redeemed),
            kind: trial.kind === 'enterprise' || trial.kind === 'stripe' ? trial.kind : null,
            code: typeof trial.code === 'string' ? trial.code : null,
            trialDays: typeof trial.trialDays === 'number' ? trial.trialDays : null,
            requiresCard: trial.requiresCard !== false,
            message: typeof trial.message === 'string' ? trial.message : null
          }
          : null);
        enterpriseTrialRef.current = Boolean(trial?.redeemed) && trial?.kind === 'enterprise';
        const token = response.data.token;
        const userData = response.data.user || {
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName
        };
        localStorage.setItem('privacypal_token', token);
        localStorage.setItem('privacypal_user', JSON.stringify(userData));
        axios.defaults.headers.common['x-access-token'] = token;
        // The user-effect above routes to pricing/card or straight to
        // onboarding/dashboard (prepaid seats skip the card step).
        await refreshUser();
      } else {
        setCodeError(response.data?.error || t`Registration failed`);
      }
    } catch (err: any) {
      const data = err.response?.data;
      setCodeError(data?.error || err.message || t`Verification failed`);
    } finally {
      setVerifyBusy(false);
    }
  };

  const handleResendCode = async () => {
    setResendBusy(true);
    setResendMessage('');
    try {
      await axios.post(`${API_BASE_URL}/api/user/signup/start`, { email: formData.email.trim() });
      setResendMessage(t`If that address can receive mail, we've sent a new code.`);
      setResendCooldown(60);
    } catch {
      setResendMessage(t`If that address can receive mail, we've sent a new code.`);
    } finally {
      setResendBusy(false);
    }
  };

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const startFree = () => {
    navigate('/onboarding');
  };

  /**
   * The Max price as the server prices it for this visitor's promo code.
   *
   * Nothing here multiplies a list price by a percentage: Stripe owns what a
   * coupon is worth, and a client that re-derives it drifts the first time a
   * coupon is amount-off or capped. Only fetched on the plan step, and only
   * when there is a code to price, because the endpoint is rate limited.
   */
  const { offer } = useMaxOffer({
    promoCode: codes.promoCode,
    plan: 'monthly',
    seats: 1,
    // Fetched as soon as there is a code to price, not just on the plan step:
    // a visitor who followed a partner link should see their discount
    // confirmed before they type anything, which is also the moment they are
    // deciding whether the link was real.
    enabled: !!codes.promoCode,
  });
  const maxPricing = offerPricing(offer);
  const maxPromo = offer?.promo && offer.promo.valid ? offer.promo : null;
  const maxPromoDuration = promoDuration(offer?.promo ?? null);
  // An enterprise code redeemed at registration: Max is already live for the
  // whole company, no card was taken and there is nothing to cancel.
  const enterpriseTrialActive = Boolean(registerTrial?.redeemed) && registerTrial?.kind === 'enterprise';
  // A code was sent and did not apply. Not an error: the account is real, and
  // the customer is told why rather than left wondering.
  const trialRejected = registerTrial && !registerTrial.redeemed ? registerTrial : null;

  const stepNumber = step === 'verify-code' ? '2' : '3';
  const ruleLabels: Record<string, string> = {
    length: t`At least 8 characters`,
    uppercase: t`An uppercase letter`,
    lowercase: t`A lowercase letter`,
    number: t`A number`,
  };

  if (user && loadingAccount) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#050a0f', color: '#e6f1ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="spinner" style={{ borderColor: 'rgba(51, 102, 153, 0.3)', borderTopColor: '#336699' }}></div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        :root { --cobalt: #336699; --cobalt-dark: #1a3a5a; --cobalt-light: #4a86c2; --glass: rgba(255, 255, 255, 0.03); --glass-border: rgba(255, 255, 255, 0.1); }
        body { background-color: #050a0f; color: #e6f1ff; font-family: 'Inter', sans-serif; overflow-x: hidden; margin: 0; }
        .mono { font-family: 'JetBrains Mono', monospace; }
        .bg-mesh { position: fixed; top: 0; left: 0; width: 100%; height: 100%; z-index: -1; background: radial-gradient(circle at 20% 30%, rgba(51, 102, 153, 0.15) 0%, transparent 50%), radial-gradient(circle at 80% 70%, rgba(51, 102, 153, 0.1) 0%, transparent 50%); }
        .velocity-line { position: absolute; width: 1px; height: 100px; background: linear-gradient(to bottom, transparent, var(--cobalt), transparent); opacity: 0.3; animation: moveDown 8s linear infinite; }
        @keyframes moveDown { 0% { transform: translateY(-100vh); } 100% { transform: translateY(100vh); } }
        .vitreous-card { background: var(--glass); backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px); border: 1px solid var(--glass-border); border-top: 1px solid rgba(255, 255, 255, 0.2); box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5); transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1); }
        .vitreous-card:hover { border-color: var(--cobalt); background: rgba(51, 102, 153, 0.05); }
        .hero-text { font-size: clamp(3rem, 8vw, 6rem); line-height: 0.9; letter-spacing: -0.04em; background: linear-gradient(180deg, #fff 0%, rgba(255,255,255,0.6) 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; padding-bottom: 0.15em; overflow: visible; }
        .sso-button { display: flex; align-items: center; justify-content: center; gap: 12px; padding: 12px; border-radius: 8px; border: 1px solid var(--glass-border); background: rgba(255,255,255,0.05); transition: all 0.2s ease; font-weight: 500; color: #e6f1ff; cursor: pointer; width: 100%; }
        .sso-button:hover { background: rgba(255,255,255,0.1); transform: translateY(-1px); }
        .input-field { background: rgba(0,0,0,0.3); border: 1px solid var(--glass-border); padding: 14px 16px; border-radius: 8px; width: 100%; outline: none; transition: border-color 0.3s ease; color: #e6f1ff; font-family: 'Inter', sans-serif; }
        .input-field::placeholder { color: rgba(230, 241, 255, 0.4); }
        .input-field:focus { border-color: var(--cobalt); }
        .primary-btn { background: var(--cobalt); color: white; padding: 14px; border-radius: 8px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; transition: all 0.3s ease; box-shadow: 0 0 20px rgba(51, 102, 153, 0.4); border: none; cursor: pointer; width: 100%; }
        .primary-btn:hover { background: var(--cobalt-light); transform: scale(1.02); box-shadow: 0 0 30px rgba(51, 102, 153, 0.6); }
        .error-message { background: rgba(239, 68, 68, 0.1); border: 1px solid rgba(239, 68, 68, 0.3); color: #ff6b6b; padding: 12px; border-radius: 8px; font-size: 0.875rem; margin-bottom: 1rem; }
      `}</style>

      <div style={{ backgroundColor: '#050a0f', color: '#e6f1ff', fontFamily: "'Inter', sans-serif", overflowX: 'hidden', margin: 0, minHeight: '100vh' }}>
        <div className="bg-mesh"></div>
        <div ref={velocityLinesContainerRef} style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: -1, pointerEvents: 'none' }}></div>

        <nav style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1.5rem 2rem', maxWidth: '80rem', margin: '0 auto', width: '100%', position: 'relative', zIndex: 10 }}>
          <img src="/privacypal-logo-light.png" alt="PrivacyPal" style={{ height: '72px', maxHeight: '72px', width: 'auto', display: 'block' }} />
          {step !== 'identity' && (
            <span className="mono" style={{ fontSize: '0.75rem', color: '#336699', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase' }}>
              <Trans>Step {stepNumber} of 3</Trans>
            </span>
          )}
        </nav>

        <main style={{ maxWidth: '80rem', margin: '0 auto', padding: '3rem 2rem 6rem', position: 'relative', zIndex: 10, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '4rem', alignItems: 'center' }}>
          <div className="reveal" style={{ animationDelay: '0.1s', paddingBottom: '60px' }}>
            <div style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <span className="mono" style={{ fontSize: '0.75rem', color: '#336699', fontWeight: 700, letterSpacing: '0.3em', textTransform: 'uppercase' }}><Trans>Get Started Free</Trans></span>
            </div>
            <h1 className="hero-text" style={{ fontWeight: 800, marginBottom: '1rem' }}>
              <Trans>Make AI Safe for Work.<br />Instantly.</Trans>
            </h1>
            <p style={{ fontSize: '1.25rem', color: 'rgba(147, 197, 253, 0.6)', maxWidth: '32rem', marginBottom: '3rem', lineHeight: '1.75' }}>
              <Trans>
                The first-ever Privacy Agent designed specifically for the Generative AI era. Stop the manual redaction. Stop the data leaks. Get 100% accurate results without the risk.
              </Trans>
            </p>
          </div>

          <div className="vitreous-card" style={{ borderRadius: '32px', padding: '2.5rem', maxWidth: '28rem', margin: '0 auto', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: '-6rem', right: '-6rem', width: '12rem', height: '12rem', background: '#336699', opacity: 0.2, filter: 'blur(80px)' }}></div>

            {step === 'identity' && (
              <>
                <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.5rem' }}><Trans>Create Your Account</Trans></h2>
                <p style={{ color: 'rgba(255, 255, 255, 0.5)', marginBottom: '2rem', fontSize: '0.875rem' }}><Trans>Enter your details to get started with PrivacyPal.</Trans></p>

                {/* The code the visitor arrived with, confirmed before they
                    spend anything on this form. Price from the server; a
                    referral code is deliberately not shown, because it buys
                    the customer nothing and only credits the partner. */}
                {maxPricing.discounted && maxPromo && (
                  <div style={{ padding: '0.9rem 1rem', marginBottom: '1.5rem', borderRadius: '10px', background: 'rgba(212, 175, 55, 0.08)', border: '1px solid rgba(212, 175, 55, 0.4)', fontSize: '0.8rem', color: '#e8c96a', lineHeight: 1.6 }}>
                    <Trans>
                      Code <strong>{maxPromo.code}</strong> is live: PrivacyPal Max at {maxPricing.perSeatLabel}/seat/mo instead of {maxPricing.listLabel}. Create your account and it carries through to checkout.
                    </Trans>
                  </div>
                )}

                {/* An enterprise trial code is worth saying out loud too: it
                    is the difference between a card and no card. */}
                {codes.trialCode && !maxPricing.discounted && (
                  <div style={{ padding: '0.9rem 1rem', marginBottom: '1.5rem', borderRadius: '10px', background: 'rgba(212, 175, 55, 0.08)', border: '1px solid rgba(212, 175, 55, 0.4)', fontSize: '0.8rem', color: '#e8c96a', lineHeight: 1.6 }}>
                    <Trans>
                      Trial code <strong>{codes.trialCode}</strong> is attached. We apply it the moment your account is created.
                    </Trans>
                  </div>
                )}

                {error && <div className="error-message">{error}</div>}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '2rem' }}>
                  <button type="button" className="sso-button" onClick={() => ssoLogin('google')}>
                    <svg style={{ width: '1.25rem', height: '1.25rem' }} viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.67l-3.57-2.77c-1 .67-2.27 1.07-3.71 1.07-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 12-4.53z"/>
                    </svg>
                    <Trans>Sign up with Google</Trans>
                  </button>
                  <button type="button" className="sso-button" onClick={() => ssoLogin('microsoft')}>
                    <svg style={{ width: '1.25rem', height: '1.25rem' }} viewBox="0 0 23 23">
                      <path fill="#f3f3f3" d="M0 0h11v11H0zM12 0h11v11H12zM0 12h11v11H0zM12 12h11v11H12z"/>
                    </svg>
                    <Trans>Sign up with Microsoft</Trans>
                  </button>
                </div>
                <div style={{ position: 'relative', marginBottom: '2rem' }}>
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center' }}><div style={{ width: '100%', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}></div></div>
                  <div style={{ position: 'relative', display: 'flex', justifyContent: 'center', fontSize: '0.75rem', textTransform: 'uppercase' }}>
                    <span style={{ background: '#0c141d', padding: '0 0.5rem', color: 'rgba(255, 255, 255, 0.3)', letterSpacing: '0.1em' }}><Trans>Or via Email</Trans></span>
                  </div>
                </div>
                <form onSubmit={handleIdentitySubmit} noValidate style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div>
                    <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'rgba(255, 255, 255, 0.4)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.5rem', display: 'block' }}><Trans>Full Name</Trans></label>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                      <input type="text" placeholder={t`First`} value={formData.firstName} onChange={(e) => { setFormData({ ...formData, firstName: e.target.value }); if (fieldErrors.firstName) setFieldErrors((prev) => ({ ...prev, firstName: undefined })); }} className="input-field" style={fieldErrors.firstName ? { borderColor: '#ff6b6b' } : undefined} aria-invalid={!!fieldErrors.firstName} />
                      <input type="text" placeholder={t`Last`} value={formData.lastName} onChange={(e) => { setFormData({ ...formData, lastName: e.target.value }); if (fieldErrors.lastName) setFieldErrors((prev) => ({ ...prev, lastName: undefined })); }} className="input-field" style={fieldErrors.lastName ? { borderColor: '#ff6b6b' } : undefined} aria-invalid={!!fieldErrors.lastName} />
                    </div>
                    {fieldErrors.firstName && <p style={fieldErrorStyle}>{fieldErrors.firstName}</p>}
                    {fieldErrors.lastName && <p style={fieldErrorStyle}>{fieldErrors.lastName}</p>}
                  </div>
                  <div>
                    <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'rgba(255, 255, 255, 0.4)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.5rem', display: 'block' }}><Trans>Work Email</Trans></label>
                    <input type="email" placeholder="name@company.com" value={formData.email} onChange={(e) => { setFormData({ ...formData, email: e.target.value }); if (fieldErrors.email) setFieldErrors((prev) => ({ ...prev, email: undefined })); }} className="input-field" style={fieldErrors.email ? { borderColor: '#ff6b6b' } : undefined} aria-invalid={!!fieldErrors.email} />
                    {fieldErrors.email && <p style={fieldErrorStyle}>{fieldErrors.email}</p>}
                  </div>
                  <div>
                    <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'rgba(255, 255, 255, 0.4)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.5rem', display: 'block' }}><Trans>Password</Trans></label>
                    <input type="password" placeholder="••••••••" value={formData.password} onChange={(e) => { setFormData({ ...formData, password: e.target.value }); if (fieldErrors.password) setFieldErrors((prev) => ({ ...prev, password: undefined })); }} className="input-field" style={fieldErrors.password ? { borderColor: '#ff6b6b' } : undefined} aria-invalid={!!fieldErrors.password} />
                    <ul style={{ listStyle: 'none', padding: 0, margin: '0.5rem 0 0', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      {passwordRules(formData.password).map((rule) => {
                        const color = rule.met ? '#00ffaa' : (submitAttempted ? '#ff6b6b' : 'rgba(255, 255, 255, 0.4)');
                        return (
                          <li key={rule.id} style={{ fontSize: '11px', color, display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span aria-hidden>{rule.met ? '✓' : '○'}</span>{ruleLabels[rule.id]}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                  <div>
                    <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'rgba(255, 255, 255, 0.4)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.5rem', display: 'block' }}><Trans>Confirm Password</Trans></label>
                    <input type="password" placeholder="••••••••" value={formData.confirmPassword} onChange={(e) => { setFormData({ ...formData, confirmPassword: e.target.value }); if (fieldErrors.confirmPassword) setFieldErrors((prev) => ({ ...prev, confirmPassword: undefined })); }} className="input-field" style={fieldErrors.confirmPassword ? { borderColor: '#ff6b6b' } : undefined} aria-invalid={!!fieldErrors.confirmPassword} />
                    {fieldErrors.confirmPassword && <p style={fieldErrorStyle}>{fieldErrors.confirmPassword}</p>}
                  </div>
                  <button type="submit" className="primary-btn" style={{ marginTop: '1rem' }}><Trans>Continue</Trans></button>
                </form>
                <p style={{ fontSize: '10px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.3)', marginTop: '1.5rem', lineHeight: '1.75' }}>
                  <Trans>
                    By continuing, you agree to our <a href="https://privacypal.ai/terms-of-service" target="_blank" rel="noopener noreferrer">Terms of Service</a>.
                  </Trans>
                </p>
              </>
            )}

            {step === 'verify-code' && (
              <>
                <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.5rem' }}><Trans>Verify your email</Trans></h2>
                <p style={{ color: 'rgba(255, 255, 255, 0.6)', marginBottom: '1.5rem', fontSize: '0.875rem', lineHeight: 1.6 }}>
                  <Trans>
                    If <strong style={{ color: 'rgba(255,255,255,0.9)' }}>{formData.email}</strong> can receive mail, we've sent it a 6-digit code. Enter it below to verify your address. Your account is only created after this step.
                  </Trans>
                </p>
                {codeError && <div className="error-message">{codeError}</div>}
                <form onSubmit={handleVerifyCode}>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="\d{6}"
                    maxLength={6}
                    placeholder="000000"
                    value={signupCode}
                    onChange={(e) => { setSignupCode(e.target.value.replace(/\D/g, '')); setCodeError(''); }}
                    className="input-field mono"
                    style={{ textAlign: 'center', fontSize: '1.75rem', letterSpacing: '0.5em', marginBottom: '1rem' }}
                    autoFocus
                  />
                  <button type="submit" className="primary-btn" disabled={verifyBusy || signupCode.length !== 6} style={{ marginBottom: '0.75rem', opacity: verifyBusy || signupCode.length !== 6 ? 0.6 : 1 }}>
                    {verifyBusy ? t`Verifying…` : t`Verify & create account`}
                  </button>
                </form>
                <p style={{ fontSize: '0.8rem', textAlign: 'center', color: 'rgba(255,255,255,0.5)', marginBottom: '0.75rem' }}>
                  <Trans>The code expires in 10 minutes. Check your spam folder if you don't see it.</Trans>
                </p>
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={resendBusy || resendCooldown > 0}
                  className="primary-btn"
                  style={{ background: 'rgba(255,255,255,0.08)', marginBottom: '0.75rem', opacity: resendBusy || resendCooldown > 0 ? 0.6 : 1 }}
                >
                  {resendBusy ? t`Sending…` : resendCooldown > 0 ? t`Resend code (${resendCooldown}s)` : t`Resend code`}
                </button>
                {resendMessage && (
                  <p style={{ fontSize: '0.8rem', textAlign: 'center', color: 'rgba(255,255,255,0.6)', marginBottom: '0.75rem' }}>{resendMessage}</p>
                )}
                <button
                  type="button"
                  onClick={() => { setStep('identity'); setResendMessage(''); setCodeError(''); }}
                  style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem', cursor: 'pointer', width: '100%', padding: '0.5rem' }}
                >
                  <Trans>← Use a different email</Trans>
                </button>
              </>
            )}

            {step === 'plan' && enterpriseTrialActive && (
              <>
                <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.5rem' }}><Trans>Your Max trial is live.</Trans></h2>
                <p style={{ color: 'rgba(255, 255, 255, 0.6)', marginBottom: '1.5rem', fontSize: '0.875rem', lineHeight: 1.6 }}>
                  {registerTrial?.trialDays != null ? (
                    <Trans>
                      Code <strong style={{ color: '#e8c96a' }}>{registerTrial.code}</strong> switched PrivacyPal Max on for your whole company for {registerTrial.trialDays} days. No card was taken and there is nothing to cancel: when the trial ends your team moves to Pro.
                    </Trans>
                  ) : (
                    <Trans>
                      Code <strong style={{ color: '#e8c96a' }}>{registerTrial?.code}</strong> switched PrivacyPal Max on for your whole company. No card was taken and there is nothing to cancel: when the trial ends your team moves to Pro.
                    </Trans>
                  )}
                </p>
                <div style={{ padding: '1.5rem', background: 'rgba(212, 175, 55, 0.08)', borderRadius: '12px', border: '1px solid rgba(212, 175, 55, 0.45)', marginBottom: '1rem' }}>
                  <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.875rem', marginBottom: '1rem' }}>
                    <Trans>Everything is unlocked: Cowork, Claude Code, Codex, Copilot, agents and the APIs, plus Connections, Private MCP, API access, team seats and full usage history.</Trans>
                  </div>
                  <button type="button" className="primary-btn" onClick={startFree}>
                    <Trans>Set up my team</Trans>
                  </button>
                </div>
              </>
            )}

            {step === 'plan' && !enterpriseTrialActive && (
              <>
                <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.5rem' }}><Trans>You&apos;re in. Choose how you start.</Trans></h2>
                <p style={{ color: 'rgba(255, 255, 255, 0.5)', marginBottom: '1.5rem', fontSize: '0.875rem' }}>
                  <Trans>No credit card needed for Pro. Upgrade whenever you&apos;re ready.</Trans>
                </p>
                {error && <div className="error-message">{error}</div>}

                {/* A trial code was sent and did not apply. The account is
                    real either way, so this is information, not an error. */}
                {trialRejected && (
                  <div style={{ padding: '0.9rem 1rem', marginBottom: '1rem', borderRadius: '10px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.12)', fontSize: '0.8rem', color: 'rgba(255,255,255,0.65)', lineHeight: 1.6 }}>
                    {trialRejected.message || <Trans>We could not apply that trial code. Your account is set up on the free plan.</Trans>}
                  </div>
                )}

                {/* Free plan: the default, one click through. */}
                <div style={{ padding: '1.5rem', background: 'rgba(255,255,255,0.05)', borderRadius: '12px', border: '1px solid var(--cobalt)', marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '1.25rem', fontWeight: 700 }}><Trans>PrivacyPal Pro</Trans></span>
                    <span style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', padding: '3px 8px', borderRadius: '999px', border: '1px solid #00ffaa', color: '#00ffaa' }}><Trans>Free</Trans></span>
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.875rem', marginBottom: '1rem' }}>
                    <Trans>Real-time protection for ChatGPT, Claude, Gemini, Grok and Perplexity chats, attachments included. One person, free forever.</Trans>
                  </div>
                  <button type="button" className="primary-btn" onClick={startFree}>
                    <Trans>Start Free</Trans>
                  </button>
                </div>

                {/* Max: two ways in. A card, charged immediately in the
                    checkout modal, or a company-wide trial. */}
                <div style={{ padding: '1.5rem', background: 'rgba(255,255,255,0.03)', borderRadius: '12px', border: '1px solid rgba(212, 175, 55, 0.45)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '1.25rem', fontWeight: 700 }}><Trans>PrivacyPal Max</Trans></span>
                    {maxPricing.discounted ? (
                      <span className="mono" style={{ fontSize: '0.8rem', color: '#e8c96a' }}>
                        <span style={{ textDecoration: 'line-through', color: 'rgba(255,255,255,0.4)', marginRight: '0.4rem' }}>{maxPricing.listLabel}</span>
                        <Trans>{maxPricing.perSeatLabel}/seat/mo</Trans>
                      </span>
                    ) : maxPrices?.monthly ? (
                      <span className="mono" style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.7)' }}>{maxPrices.monthly}</span>
                    ) : null}
                  </div>

                  {/* The discount, in the coupon's own terms. Stripe priced
                      it; nothing on this page derived it. */}
                  {maxPromo && maxPricing.discounted && (
                    <div style={{ fontSize: '0.78rem', color: '#e8c96a', marginBottom: '0.75rem' }}>
                      {maxPromoDuration.kind === 'repeating' ? (
                        <Trans>Code {maxPromo.code} applied for your first {maxPromoDuration.months} months.</Trans>
                      ) : maxPromoDuration.kind === 'once' ? (
                        <Trans>Code {maxPromo.code} applied to your first month.</Trans>
                      ) : maxPromoDuration.kind === 'forever' ? (
                        <Trans>Code {maxPromo.code} applied for as long as you stay.</Trans>
                      ) : (
                        <Trans>Code {maxPromo.code} applied.</Trans>
                      )}
                    </div>
                  )}

                  <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.875rem', marginBottom: '1rem' }}>
                    <Trans>Everything in Pro, plus protection for Cowork, Claude Code, Codex, Copilot, agents and the APIs, with Connections, Private MCP, API access, team seats and full usage history.</Trans>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setUpgradePath('card'); setUpgradeOpen(true); }}
                    style={{ width: '100%', padding: '14px', borderRadius: '8px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', border: '1px solid rgba(212, 175, 55, 0.6)', background: 'rgba(212, 175, 55, 0.12)', color: '#e8c96a', cursor: 'pointer' }}
                  >
                    <Trans>Go Max</Trans>
                  </button>
                  <button
                    type="button"
                    onClick={() => { setUpgradePath('trial'); setUpgradeOpen(true); }}
                    style={{ width: '100%', marginTop: '0.6rem', padding: '12px', borderRadius: '8px', fontWeight: 600, border: '1px solid rgba(255,255,255,0.14)', background: 'transparent', color: 'rgba(255,255,255,0.7)', cursor: 'pointer', fontSize: '0.85rem' }}
                  >
                    <Trans>I have a trial code for my company</Trans>
                  </button>
                  <p style={{ fontSize: '0.75rem', textAlign: 'center', color: 'rgba(255,255,255,0.35)', marginTop: '0.75rem', marginBottom: 0 }}>
                    <Trans>
                      Evaluating Max for a team? <a href={trialCallUrl({ source: 'signup', referralCode: codes.referralCode })} target="_blank" rel="noopener noreferrer" style={{ color: 'rgba(147, 197, 253, 0.8)' }}>Book a trial call</a> and we will issue a code.
                    </Trans>
                  </p>
                </div>

                <UpgradeModal
                  open={upgradeOpen}
                  onClose={() => setUpgradeOpen(false)}
                  onUpgraded={() => navigate('/onboarding')}
                  source="signup"
                  initialPromoCode={codes.promoCode}
                  initialTrialCode={codes.trialCode}
                  initialPath={upgradePath}
                />
              </>
            )}
          </div>
        </main>

        <footer style={{ position: 'fixed', bottom: 0, width: '100%', background: 'rgba(5, 10, 15, 0.8)', backdropFilter: 'blur(24px)', borderTop: '1px solid rgba(255, 255, 255, 0.05)', padding: '1rem 0', zIndex: 20 }}>
          <div style={{ maxWidth: '80rem', margin: '0 auto', padding: '0 2rem', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <div style={{ fontSize: '10px', fontFamily: "'JetBrains Mono', monospace", color: 'rgba(255, 255, 255, 0.2)', textTransform: 'uppercase', letterSpacing: '0.2em' }}><Trans>&copy; 2026 WEB3 REX INC. All Rights Reserved.</Trans></div>
          </div>
        </footer>
      </div>
    </>
  );
}
